import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  Link,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Form, Formik, FormikProps } from "formik";
import { useDropzone, FileRejection } from "react-dropzone";
import { StateReportModel } from "../../models/StateReportModel";
import {
  buildFileNowValidationSchema,
  EMPTY_FILING_FIELD_REQUIREMENTS,
  FilingFieldRequirements,
  fileNowFieldToDbValue,
  isDisableZeroReportEnabled,
  isZeroReportType,
  normalizeFileNowFieldValue,
  parseFilingFieldRequirementsFromSchedule,
} from "../../models/filingScheduleAdminTypes";
import moment from "moment";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import Swal from "sweetalert2";
import useAuth from "../../context/AuthProvider";
import { apiClient } from "../../helper/api";
import { formatUtcToLocal, toUtcDateString, mstDateStringToUtcIsoString } from "../../helper/utils";
import { loadFileForViewer } from "../../services/api/documentViewerService";
import { StateReportDocumentViewerModal } from "./StateReportDocumentViewerModal";
import StateReportFilerNotes, { FiledNotesSubmitData } from "./stateReportFilerNotes";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { NO_ACTION_REQUIRED_MARKER } from "../../helper/noClientActionRequired";
import {
  buildNoticeViewModelFromRowAndPreview,
  stripUrlsFromInstructions,
  getNoticeSummaryItems,
  getNoticePaymentItems,
  getNoticeStatusItems,
  formatNoticeTaxAmount,
  NoticeReportSummaryCard,
  NoticeFilingDetailsCard,
  NoticeInstructionsAndNotesCard,
  NoticeStatusCard,
  noticeSectionTitleSx,
  noticeScreenCardSx,
} from "./stateReportNoticeLayout";

interface FileAttachment {
  id: string;
  type: string;
  file: File | null;
}

interface FilingScheduleData {
  [key: string]: any;
}

interface ScheduleDataByType {
  premiumBearing: FilingScheduleData | null;
  zeroReport: FilingScheduleData | null;
  disableZeroReport?: boolean | number | string;
}

interface CustomProps {
  name: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
}

interface Iprops {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (data: {
    typeOfReport: string;
    taxAmount: string;
    paymentType: string;
    payTo: string;
    address: string;
    instructions: string;
    additionalNotes: string;
    largeReport: string;
    largeReportFee: string;
    attachments: FileAttachment[];
  }) => void;
  selectedRow: StateReportModel | null;
  scheduleDataByType: ScheduleDataByType | null;
  states: { stateId: number; stateName: string }[];
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="$"
        decimalScale={2}
      />
    );
  },
);

// File Dropzone Component
const DOCUMENT_DROPZONE_ACCEPT = {
  "application/pdf": [".pdf"],
  "image/*": [],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    [".docx"],
  "application/msword": [".doc"],
};

const EXCEL_DROPZONE_ACCEPT = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.ms-excel": [".xls"],
};

function FileDropzone({
  label,
  onFileDrop,
  acceptedFile,
  onViewFile,
  disabled = false,
  allowExcel = false,
}: {
  label: string;
  onFileDrop: (file: File | null) => void;
  acceptedFile: File | null;
  onViewFile?: (file: File) => void;
  disabled?: boolean;
  allowExcel?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const onDropRejected = (fileRejections: FileRejection[]) => {
    const first = fileRejections?.[0];
    const errCodes = first?.errors?.map((e) => e.code) || [];

    if (errCodes.includes("file-too-large")) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "Maximum file size 10MB.",
        confirmButtonText: "OK",
      });
      return;
    }
    if (errCodes.includes("file-invalid-type")) {
      Swal.fire({
        icon: "error",
        title: "Invalid file type",
        text: allowExcel
          ? "Only .pdf, Word, image, and Excel (.xlsx) files are allowed."
          : "Only .pdf, Word, and image files are allowed.",
        confirmButtonText: "OK",
      });
      return;
    }
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      maxFiles: 1,
      maxSize: 10 * 1024 * 1024, // 10MB
      disabled,
      accept: allowExcel
        ? { ...DOCUMENT_DROPZONE_ACCEPT, ...EXCEL_DROPZONE_ACCEPT }
        : DOCUMENT_DROPZONE_ACCEPT,
      onDrop: (files) => {
        const file = files?.[0];
        if (file) {
          onFileDrop(file);
        }
      },
      onDropRejected,
    });

  const baseStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 8,
    borderColor: "#B0BEC5",
    borderStyle: "dashed",
    backgroundColor: "#ECEFF1",
    color: "#546E7A",
    outline: "none",
    transition: "border .24s ease-in-out",
    minHeight: "120px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  const focusedStyle: Partial<React.CSSProperties> = {
    borderColor: "#2196f3",
  };

  const acceptStyle: Partial<React.CSSProperties> = {
    borderColor: "#00e676",
  };

  const rejectStyle: Partial<React.CSSProperties> = {
    borderColor: "#ff1744",
  };

  const style = useMemo(
    () =>
      ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
      }) as React.CSSProperties,
    [isFocused, isDragAccept, isDragReject],
  );

  const handleDropzoneClick = (e: React.MouseEvent) => {
    if (acceptedFile && onViewFile) {
      e.preventDefault();
      e.stopPropagation();
      onViewFile(acceptedFile);
    }
  };

  // Conditionally apply root props - disable click when file is attached
  const rootProps = getRootProps({ style });
  const inputProps = getInputProps();

  // Disable input when file is attached to prevent file picker
  if (acceptedFile && onViewFile) {
    inputProps.disabled = true;
  }

  // Override onClick when file is attached
  const handleBoxClick =
    acceptedFile && onViewFile
      ? handleDropzoneClick
      : (rootProps as any).onClick;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",
        width: "100%",
        ...(disabled ? { opacity: 0.7, pointerEvents: "none" } : {}),
      }}
    >
      <Box
        {...rootProps}
        onClick={handleBoxClick}
        sx={
          disabled
            ? { cursor: "not-allowed" }
            : acceptedFile && onViewFile
              ? { cursor: "pointer" }
              : {}
        }
      >
        <input {...inputProps} />
        {acceptedFile ? (
          <Box
            sx={{
              textAlign: "center",
              position: "relative",
              zIndex: 1,
              width: "100%",
              px: 1,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: "#1976D2",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={acceptedFile.name}
            >
              {acceptedFile.name}
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: "12px", color: "#666" }}
            >
              {(acceptedFile.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <p style={{ margin: 0 }}>Click or drag file here</p>
            <p style={{ margin: "4px 0 0 0", fontSize: "12px" }}>{label}</p>
          </Box>
        )}
      </Box>
      {/* Show upload icon when no file and hovering */}
      {!acceptedFile && isHovered && (
        <>
          {/* Transparent black overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "8px",
              zIndex: 2,
              cursor: "pointer",
            }}
            onClick={(rootProps as any).onClick}
          />
          {/* Upload icon */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "transparent",
              borderRadius: "50%",
              padding: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
            onClick={(rootProps as any).onClick}
          >
            <CloudUploadIcon
              sx={{
                color: "#ffffff",
                fontSize: "32px",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        </>
      )}
      {/* Show eye icon when file is attached and hovering */}
      {acceptedFile && isHovered && onViewFile && (
        <>
          {/* Transparent black overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "8px",
              zIndex: 2,
              cursor: "pointer",
            }}
            onClick={handleDropzoneClick}
          />
          {/* Black and white eye icon */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "transparent",
              borderRadius: "50%",
              padding: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
            onClick={handleDropzoneClick}
          >
            <VisibilityIcon
              sx={{
                color: "#ffffff",
                fontSize: "32px",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

// Renders the read-only header section used in both Edit and Preview modes
const HeaderInfo: React.FC<{
  selectedRow: StateReportModel;
  states: { stateId: number; stateName: string }[];
}> = ({ selectedRow, states }) => {
  return (
    <>
      {/* Pre-filled Header Section */}
      <Box
        sx={{
          backgroundColor: "#E3F2FD",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "24px",
        }}
      >
        <Grid container spacing={2}>
          {/* First Table: State, Report Name, Period */}
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    textAlign: "left",
                    fontSize: "20px",
                  }}
                >
                  State:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "right",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  {states.find((state) => state.stateId === selectedRow.stateId)
                    ?.stateName || "N/A"}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Report Name:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  {selectedRow.name || "N/A"}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Period:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  {selectedRow.period || "N/A"}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Second Table: Due Date, Licensee Name, Client Name */}
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    color: "#d32f2f",
                    textAlign: "left",
                    fontSize: "20px",
                  }}
                >
                  Due Date:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    color: "#d32f2f",
                    fontWeight: "bold",
                    textAlign: "right",
                    fontSize: "20px",
                  }}
                >
                  {selectedRow.dueDate
                    ? formatUtcToLocal(selectedRow.dueDate, "MM/DD/YYYY")
                    : "N/A"}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Licensee Name:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  {selectedRow.licensee || "N/A"}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "200px",
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Client Name:
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  {selectedRow.client || selectedRow.partnerName || "N/A"}
                  {Number(selectedRow.partnerCount ?? 0) > 1 && (
                    <Box component="span" sx={{ display: "block", fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
                      Filing jointly — {Number(selectedRow.partnerCount)} linked clients
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

type FileNowFormValues = {
  typeOfReport: string;
  taxAmount: string;
  paymentType: string;
  payTo: string;
  address: string;
  instructions: string;
  filingUrl: string;
  additionalNotes: string;
  largeReport: string;
  largeReportFee: string;
};

/** Auto-select sole report type and re-validate when schedule Required flags change. */
const FileNowFormEffects: React.FC<{
  scheduleDataByType: Iprops["scheduleDataByType"];
  canFileAsZeroReport: boolean;
  typeOfReport: string;
  selectTypeData: (typeOfReport: string) => FilingScheduleData | null;
  applyTypeData: (
    typeData: FilingScheduleData | null,
    setFieldValue: (field: string, value: unknown) => void,
  ) => void;
  setFieldValue: (field: string, value: unknown) => void;
  fieldRequirements: FilingFieldRequirements;
  validateForm: () => Promise<unknown>;
}> = ({
  scheduleDataByType,
  canFileAsZeroReport,
  typeOfReport,
  selectTypeData,
  applyTypeData,
  setFieldValue,
  fieldRequirements,
  validateForm,
}) => {
  useEffect(() => {
    if (!scheduleDataByType || typeOfReport) return;
    const options: string[] = [];
    if (scheduleDataByType.premiumBearing) options.push("Premium-Bearing");
    if (scheduleDataByType.zeroReport && canFileAsZeroReport) options.push("Zero Report");
    if (options.length !== 1) return;
    const only = options[0];
    setFieldValue("typeOfReport", only);
    applyTypeData(selectTypeData(only), setFieldValue);
  }, [
    scheduleDataByType,
    canFileAsZeroReport,
    typeOfReport,
    selectTypeData,
    applyTypeData,
    setFieldValue,
  ]);

  useEffect(() => {
    void validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when admin Required flags change
  }, [fieldRequirements]);

  return null;
};

const StateReportFileNowDialog: React.FC<Iprops> = ({
  open,
  handleClose,
  handleSubmit,
  selectedRow,
  scheduleDataByType,
  states,
}) => {
  const { user } = useAuth();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(
    null,
  );
  const [documentViewerIsBlobUrl, setDocumentViewerIsBlobUrl] = useState(false);
  const [documentViewerLoading, setDocumentViewerLoading] = useState(false);
  const [documentViewerFileName, setDocumentViewerFileName] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [filerNotesOpen, setFilerNotesOpen] = useState(false);
  const [filingScheduleTaxAmount, setFilingScheduleTaxAmount] = useState<string | null>(null);
  /** Schedule messaging + type for No Client Action Required Filed Notes complete. */
  const [ncaScheduleMessaging, setNcaScheduleMessaging] = useState<{
    instructions: string;
    notesToClient: string;
    typeOfReport: string;
  } | null>(null);
  const [reportInvoiceRequired, setReportInvoiceRequired] = useState(false);
  const [reconciliationListRequired, setReconciliationListRequired] = useState(false);
  const [fieldRequirements, setFieldRequirements] =
    useState<FilingFieldRequirements>(EMPTY_FILING_FIELD_REQUIREMENTS);
  const formikRef = useRef<FormikProps<FileNowFormValues>>(null);
  const [attachments, setAttachments] = useState<FileAttachment[]>([
    { id: "1", type: "Report or Invoice", file: null },
    { id: "2", type: "Reconciliation List", file: null },
    { id: "3", type: "Supplemental Attachment 1", file: null },
    { id: "4", type: "Supplemental Attachment 2", file: null },
  ]);
  const zeroReportDisabled = useMemo(
    () =>
      isDisableZeroReportEnabled(
        scheduleDataByType?.disableZeroReport ?? selectedRow?.disableZeroReport,
      ),
    [scheduleDataByType?.disableZeroReport, selectedRow?.disableZeroReport],
  );

  const canFileAsZeroReport =
    Boolean(scheduleDataByType?.zeroReport) && !zeroReportDisabled;

  const getTaxAmount = useCallback((typeData: FilingScheduleData | null): string => {
    if (!typeData) return "";
    const taxAmountVal = typeData.tax_Amount ?? typeData.Tax_Amount ?? null;
    return taxAmountVal == null ? "" : String(taxAmountVal).trim();
  }, []);

  const getMappingMessaging = useCallback((typeData: FilingScheduleData | null) => {
    if (!typeData) {
      return { instructions: "", notesToClient: "", typeOfReport: "" };
    }
    return {
      instructions: String(typeData.instructions ?? typeData.Instructions ?? "").trim(),
      notesToClient: String(
        typeData.additional_Notes
          ?? typeData.Additional_Notes
          ?? typeData.additionalNotes
          ?? "",
      ).trim(),
      typeOfReport: String(
        typeData.type_of_Report ?? typeData.Type_of_Report ?? "",
      ).trim(),
    };
  }, []);

  const isNoActionMapping = useCallback((typeData: FilingScheduleData | null): boolean => {
    if (!typeData) return false;
    const actionType = String(typeData.actionType ?? typeData.ActionType ?? "")
      .trim()
      .toLowerCase();
    return actionType === "no_action";
  }, []);

  const selectTypeData = useCallback(
    (typeOfReport: string): FilingScheduleData | null => {
      if (!scheduleDataByType) return null;
      const normalizedType = typeOfReport.trim().toLowerCase();
      if (normalizedType === "premium-bearing" || normalizedType === "premium bearing") {
        return scheduleDataByType.premiumBearing;
      }
      if (normalizedType === "zero report" || normalizedType === "zero-report") {
        if (zeroReportDisabled) return null;
        return scheduleDataByType.zeroReport;
      }
      return null;
    },
    [scheduleDataByType, zeroReportDisabled],
  );

  const applyTypeData = useCallback(
    (
      typeData: FilingScheduleData | null,
      setFieldValue: (field: string, value: any) => void,
    ) => {
      if (isNoActionMapping(typeData)) {
        setFilingScheduleTaxAmount(getTaxAmount(typeData) || "");
        setNcaScheduleMessaging(getMappingMessaging(typeData));
        setFieldRequirements(parseFilingFieldRequirementsFromSchedule(typeData));
        setFilerNotesOpen(true);
        return;
      }

      const data = typeData || {};
      const taxAmount = data.tax_Amount ?? data.Tax_Amount ?? null;
      const paymentType = data.payment_Type ?? data.Payment_Type ?? null;
      const payTo = data.pay_To ?? data.Pay_To ?? null;
      const address = data.address ?? data.Address ?? null;
      const instructions = data.instructions ?? data.Instructions ?? null;
      const filingUrl = data.uRL ?? data.URL ?? null;
      const additionalNotes = data.additional_Notes ?? data.Additional_Notes ?? null;
      const largeReport = data.large_Report ?? data.Large_Report ?? null;
      const reportInvoiceRequiredValue =
        data.report_Invoice_Required_Y_N ?? data.Report_Invoice_Required_Y_N;
      const reconciliationListRequiredValue =
        data.reconciliation_List_Required_Y_N ?? data.Reconciliation_List_Required_Y_N;

      setReportInvoiceRequired(!!reportInvoiceRequiredValue);
      setReconciliationListRequired(!!reconciliationListRequiredValue);
      setFieldRequirements(parseFilingFieldRequirementsFromSchedule(data));

      setFieldValue("taxAmount", taxAmount == null ? "" : String(taxAmount));
      setFieldValue("paymentType", paymentType || "");
      setFieldValue("payTo", payTo || "");
      setFieldValue("address", normalizeFileNowFieldValue(address));
      setFieldValue("instructions", instructions || "");
      setFieldValue(
        "filingUrl",
        filingUrl && String(filingUrl).trim().toUpperCase() !== "N/A"
          ? String(filingUrl).trim()
          : "",
      );
      setFieldValue("additionalNotes", additionalNotes || "");
      setFieldValue("largeReport", largeReport === true ? "true" : "false");
      if (largeReport !== true) {
        setFieldValue("largeReportFee", "");
      }
    },
    [getTaxAmount, getMappingMessaging, isNoActionMapping],
  );

  // Initialize form values from selectedRow or preview data (memoized)
  const initialValues = useMemo(() => {
    if (previewData) {
      return {
        typeOfReport: previewData.typeOfReport || "",
        taxAmount: previewData.taxAmount || "",
        paymentType: previewData.paymentType || "",
        payTo: previewData.payTo || "",
        address: normalizeFileNowFieldValue(previewData.address),
        instructions: previewData.instructions || "",
        filingUrl: previewData.filingUrl || "",
        additionalNotes: previewData.additionalNotes || "",
        largeReport: previewData.largeReport || "false",
        largeReportFee: previewData.largeReportFee || "",
      };
    }
    return {
      typeOfReport: "",
      taxAmount: selectedRow?.taxAmount || "",
      paymentType: selectedRow?.paymentType || "",
      payTo: "",
      address: "",
      instructions: selectedRow?.instructions || "",
      filingUrl: "",
      additionalNotes: "",
      largeReport: "false",
      largeReportFee: "",
    };
  }, [
    previewData,
    selectedRow?.largeReport,
    selectedRow?.taxAmount,
    selectedRow?.paymentType,
    selectedRow?.instructions,
  ]);

  // Restore attachments when going back to edit mode
  useEffect(() => {
    if (!isPreviewMode && previewData?.attachments) {
      setAttachments(previewData.attachments);
    }
  }, [isPreviewMode, previewData]);

  const addMoreAttachments = useCallback(() => {
    const currentLength = attachments.length;
    const maxAllowed = 12;
    const canAdd = Math.min(2, maxAllowed - currentLength);

    if (canAdd > 0) {
      const newAttachments = [];
      for (let i = 0; i < canAdd; i++) {
        const newId = String(currentLength + i + 1);
        const attachmentNumber = currentLength - 2 + i + 1;
        newAttachments.push({
          id: newId,
          type: `Supplemental Attachment ${attachmentNumber}`,
          file: null,
        });
      }
      setAttachments([...attachments, ...newAttachments]);
    }
  }, [attachments]);

  const handleFileDrop = useCallback(
    (index: number, file: File | null) => {
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = { ...updatedAttachments[index], file };
      setAttachments(updatedAttachments);
    },
    [attachments],
  );

  const removeAttachment = useCallback(
    (index: number) => {
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = { ...updatedAttachments[index], file: null };
      setAttachments(updatedAttachments);
    },
    [attachments],
  );

  useEffect(() => {
    if (!open) {
      // Reset attachments and preview mode when dialog closes
      setAttachments([
        { id: "1", type: "Report or Invoice", file: null },
        { id: "2", type: "Reconciliation List", file: null },
        { id: "3", type: "Supplemental Attachment 1", file: null },
        { id: "4", type: "Supplemental Attachment 2", file: null },
      ]);
      setIsPreviewMode(false);
      setPreviewData(null);
      setReportInvoiceRequired(false);
      setReconciliationListRequired(false);
      setFieldRequirements(EMPTY_FILING_FIELD_REQUIREMENTS);
      setFilerNotesOpen(false);
      setFilingScheduleTaxAmount(null);
      setNcaScheduleMessaging(null);
    }
  }, [open]);

  const validationSchema = useMemo(
    () => buildFileNowValidationSchema(fieldRequirements),
    [fieldRequirements],
  );

  useEffect(() => {
    if (!open || !scheduleDataByType) return;

    // Only consider mapping rows that actually exist on the schedule.
    // A missing zeroReport (e.g. when DisableZeroReport=1) must not be
    // interpreted as "no action".
    const configuredTypes = [
      scheduleDataByType.premiumBearing,
      zeroReportDisabled ? null : scheduleDataByType.zeroReport,
    ].filter((t): t is FilingScheduleData => Boolean(t));

    if (configuredTypes.length === 0) return;

    if (configuredTypes.every((t) => isNoActionMapping(t))) {
      const primaryMapping =
        scheduleDataByType.premiumBearing ||
        (!zeroReportDisabled ? scheduleDataByType.zeroReport : null);
      const taxAmountVal =
        getTaxAmount(scheduleDataByType.premiumBearing) ||
        (!zeroReportDisabled ? getTaxAmount(scheduleDataByType.zeroReport) : null);
      setFilingScheduleTaxAmount(taxAmountVal || "");
      setNcaScheduleMessaging(getMappingMessaging(primaryMapping));
      setFilerNotesOpen(true);
    }
  }, [
    getTaxAmount,
    getMappingMessaging,
    isNoActionMapping,
    open,
    scheduleDataByType,
    zeroReportDisabled,
  ]);

  const handleFormSubmit = useCallback(
    (values: any) => {
      if (zeroReportDisabled && isZeroReportType(values.typeOfReport)) {
        Swal.fire({
          title: "Not allowed",
          text: "Zero report is disabled for this filing schedule. Choose Premium-Bearing or another allowed type.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      // Field is required when API value is true: Report_Invoice_Required_Y_N / Reconciliation_List_Required_Y_N
      if (reportInvoiceRequired) {
        const reportOrInvoice = attachments.find((a) => a.type === "Report or Invoice");
        if (!reportOrInvoice?.file) {
          Swal.fire({
            title: "Required",
            text: "Please upload the Report or Invoice document.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }
      }
      if (reconciliationListRequired) {
        const reconciliationList = attachments.find((a) => a.type === "Reconciliation List");
        if (!reconciliationList?.file) {
          Swal.fire({
            title: "Required",
            text: "Please upload the Reconciliation List document.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }
      }
      setPreviewData({
        ...values,
        address: normalizeFileNowFieldValue(values.address),
        attachments: attachments,
      });
      setIsPreviewMode(true);
    },
    [attachments, reportInvoiceRequired, reconciliationListRequired, zeroReportDisabled],
  );

  const handleContinueEdit = () => {
    setIsPreviewMode(false);
  };

  const handlePublish = async () => {
    if (!previewData || !selectedRow || isPublishing) {
      return;
    }

    if (zeroReportDisabled && isZeroReportType(previewData.typeOfReport)) {
      Swal.fire({
        title: "Not allowed",
        text: "Zero report is disabled for this filing schedule. Reports cannot be filed as Zero Report.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    const publishTypeData = selectTypeData(previewData.typeOfReport || "");
    if (isNoActionMapping(publishTypeData)) {
      Swal.fire({
        title: "Not allowed",
        text: "This schedule is No Action Required (no billing). Use Filed Notes to complete the report.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsPublishing(true);
    setLoading(true);
    try {
      // Prepare the data according to StateReportModel
      const submitData: any = {
        stateReportId: selectedRow.stateReportId || 0,
        scheduleId: selectedRow.scheduleId || 0,
        stateName: selectedRow.stateName || "",
        name: selectedRow.name || "",
        period: selectedRow.period || "",
        dueDate: selectedRow.dueDate || "",
        licensee: selectedRow.licensee || "",
        licenseNumber: selectedRow.licenseNumber || "",
        client: selectedRow.client || "",
        partnerId: selectedRow.partnersId || selectedRow.partnerId || 0,
        partnersId: selectedRow.partnersId || 0,

        // Form fields
        taxAmount: previewData.taxAmount || "",
        paymentType: previewData.paymentType || "",
        notes: previewData.notes || "",
        notesToClient: previewData.notesToClient || "",
        largeReport: previewData.largeReport === "true",
        largeReportFee: previewData.largeReportFee || "",

        // Status - set to "Filed" when publishing
        status: "Filed",

        // Preserve existing fields from selectedRow
        payment: selectedRow.payment,
        report: selectedRow.report,
        wetSignature: selectedRow.wetSignature,
        notaryStamp: selectedRow.notaryStamp,
        zeroIndividual: selectedRow.zeroIndividual,
        zeroEntity: selectedRow.zeroEntity,
        mainLicensee: selectedRow.mainLicensee,

        // Add filer information
        filerName:
          `${user?.userFirstName || ""} ${user?.userLastName || user?.userlastName || ""}`.trim(),
        dateFinalReportSentToClient: toUtcDateString(),

        // Additional fields that might be needed
        payTo: previewData.payTo || "",
        address: fileNowFieldToDbValue(previewData.address),
        instructions:
          previewData.filingUrl
            ? `${(previewData.instructions || "").trim()}\n\n${previewData.filingUrl}`
            : (previewData.instructions || ""),
        typeOfReport: previewData.typeOfReport || "",
        additionalNotes: previewData.additionalNotes || "",
      };

      // Check if there are files to upload
      const filesToUpload =
        previewData.attachments?.filter(
          (att: FileAttachment) => att.file !== null,
        ) || [];

      // Use FormData for both cases (with or without files) - single API call
      const formData = new FormData();
      // Append JSON data as a string field (always included)
      formData.append("data", JSON.stringify(submitData));
      // Append files if any exist
      if (filesToUpload.length > 0) {
        filesToUpload.forEach((attachment: FileAttachment, index: number) => {
          if (attachment.file) {
            formData.append(`file_${index}`, attachment.file);
            formData.append(`fileType_${index}`, attachment.type);
            formData.append(`fileName_${index}`, attachment.file.name);
          }
        });
        formData.append("fileCount", filesToUpload.length.toString());
      } else {
        // No files - still append fileCount as 0
        formData.append("fileCount", "0");
      }

      // Single API call using FormData (works for both with and without files)
      const response = await apiClient("/StateReport/FileNowStateReport", {
        method: "POST",
        headers: new Headers({
          // Don't set Content-Type header - browser will set it with boundary for FormData
          Authorization: `Bearer ${user?.token}`,
        }),
        body: formData,
      });

      if (response?.status !== true) {
        throw new Error(response?.error);
      }
      await Swal.fire({
        title: "Success!",
        text: response?.message,
        icon: "success",
        confirmButtonText: "OK",
      });

      handleClose();
      handleSubmit?.(previewData);
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text:
          error?.message || "Failed to publish state report. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsPublishing(false);
      setLoading(false);
    }
  };

  const handleViewFile = async (file: File) => {
    if (!user?.token) {
      Swal.fire({
        title: "Error",
        text: "You must be signed in to preview documents.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setDocumentViewerOpen(true);
    setDocumentViewerFileName(file.name);
    setDocumentViewerUrl(null);
    setDocumentViewerIsBlobUrl(false);
    setDocumentViewerLoading(true);

    try {
      const result = await loadFileForViewer(file, user.token);
      setDocumentViewerUrl(result.viewerUrl);
      setDocumentViewerIsBlobUrl(result.isBlobUrl);
    } catch (error) {
      console.error("Error loading file for preview:", error);
      setDocumentViewerOpen(false);
      Swal.fire({
        title: "Preview unavailable",
        text:
          error instanceof Error
            ? error.message
            : "Unable to preview this file. Please download it instead.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    } finally {
      setDocumentViewerLoading(false);
    }
  };

  const handleCloseDocumentViewer = () => {
    if (documentViewerUrl && documentViewerIsBlobUrl) {
      URL.revokeObjectURL(documentViewerUrl);
    }
    setDocumentViewerOpen(false);
    setDocumentViewerUrl(null);
    setDocumentViewerIsBlobUrl(false);
    setDocumentViewerFileName(null);
    setDocumentViewerLoading(false);
  };

  const handleDownloadFile = (file: File) => {
    // Create a blob URL and trigger download
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = async () => {
    if (!previewData?.attachments || previewData.attachments.length === 0) {
      Swal.fire({
        title: "Error",
        text: "No documents available to download.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setDownloadingAll(true);
    try {
      // Prepare documents array with documentName and blobAddress (base64 data URL)
      const documentsPayloadPromises = previewData.attachments
        .filter((attachment: FileAttachment) => attachment.file !== null)
        .map(async (attachment: FileAttachment) => {
          if (attachment.file) {
            // Convert File to base64 data URL
            return new Promise<{ documentName: string; blobAddress: string }>(
              (resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  resolve({
                    documentName: attachment.file!.name || "",
                    blobAddress: reader.result as string, // This will be a data URL (base64)
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(attachment.file!);
              },
            );
          }
          return null;
        })
        .filter((promise: any) => promise !== null);

      const documentsPayload = await Promise.all(documentsPayloadPromises);

      if (documentsPayload.length === 0) {
        Swal.fire({
          title: "Error",
          text: "No valid documents to download.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setDownloadingAll(false);
        return;
      }

      const response = await apiClient(
        "/StateReport/generateComprehensiveStateReportDocumentsZip",
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          }),
          body: JSON.stringify({
            documents: documentsPayload,
          }),
        },
      );

      if (response?.status && response?.data) {
        // Handle different response types
        if (response.data.zip_url) {
          // Direct URL download
          window.open(response.data.zip_url, "_blank");
        } else if (response.data.zipBase64) {
          // Base64 encoded ZIP
          const byteCharacters = atob(response.data.zipBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/zip" });

          const fileName = `StateReportDocuments_${selectedRow?.stateReportId || "preview"}_${new Date().toISOString().split("T")[0]}.zip`;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          Swal.fire({
            title: "Warning",
            text:
              response?.message ||
              "Download initiated, but no file data received.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          setDownloadingAll(false);
          return;
        }

        // No need to clean up base64 data URLs

        Swal.fire({
          title: "Success!",
          text: response?.message || "All documents downloaded successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        throw new Error(response?.message || "Failed to generate ZIP file.");
      }
    } catch (error: any) {
      console.error("Error downloading all documents:", error);
      Swal.fire({
        title: "Error",
        text:
          error?.message || "Failed to download documents. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const fileNowPreviewNoticeVm = useMemo(() => {
    if (!isPreviewMode || !previewData || !selectedRow) return null;
    return buildNoticeViewModelFromRowAndPreview(selectedRow, previewData);
  }, [isPreviewMode, previewData, selectedRow]);

  if (!selectedRow) {
    return null;
  }

  return (
    <>
    <Dialog
      open={open && !filerNotesOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle className="card-title">
        {isPreviewMode ? "Preview and Publish" : "File State Report"}
      </DialogTitle>
      <div
        style={{
          position: "fixed",
          top: "0",
          left: "0",
          zIndex: "9990",
          width: "100%",
        }}
      >
        {loading && (
          <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />
        )}
      </div>
      {isPreviewMode ? (
        // Preview Mode
        <DialogContent dividers>
          {/* Preview Header */}
          <Box
            sx={{
              backgroundColor: "yellow",
              marginBottom: "12px",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "31px",
                letterSpacing: "-0.009em",
                fontWeight: "1000",
                lineHeight: "1.5",
                textTransform: "uppercase",
              }}
            >
              THIS IS A PREVIEW
            </Typography>
          </Box>
          <Box sx={{ marginBottom: "24px", fontSize: "24px", width: "70%", marginLeft: "auto", marginRight: "auto" }}>
            <Typography sx={{ fontSize: "1.5rem", margin: 0, letterSpacing: "-0.009em", fontWeight: "400", lineHeight: "1.5" }}>
              IMPORTANT: This is a Preview of how the report
              will appear to the client.{" "}
              <strong style={{ fontSize: "1.5rem", letterSpacing: "-0.009em", fontWeight: "1000", lineHeight: "1.5" }}>
                <span style={{ color: "#d32f2f" }}>Double Check your work</span>{" "}
                before you <span style={{ color: "#d32f2f" }}>Publish</span> to
                the Client Portal.
              </strong>
            </Typography>
          </Box>

          {fileNowPreviewNoticeVm && (() => {
            const { instructionsOnly, filingUrl } = stripUrlsFromInstructions(
              fileNowPreviewNoticeVm.instructions,
            );
            return (
              <Box
                sx={{
                  p: 3,
                  mb: 2,
                  backgroundColor: "#f5f8fb",
                  borderRadius: 1,
                }}
              >
                <Grid
                  container
                  spacing={3}
                  sx={{
                    "@media print": {
                      rowGap: 0,
                      columnGap: 0,
                    },
                  }}
                >
                  <NoticeReportSummaryCard
                    items={getNoticeSummaryItems(fileNowPreviewNoticeVm, states)}
                  />
                  <NoticeFilingDetailsCard
                    taxAmountFormatted={
                      fileNowPreviewNoticeVm.taxAmount != null &&
                      String(fileNowPreviewNoticeVm.taxAmount).trim() !== ""
                        ? formatNoticeTaxAmount(fileNowPreviewNoticeVm.taxAmount)
                        : "N/A"
                    }
                    paymentItems={getNoticePaymentItems(fileNowPreviewNoticeVm)}
                  />
                  <NoticeInstructionsAndNotesCard
                    instructionsOnly={instructionsOnly}
                    filingUrl={filingUrl}
                    additionalNotes={fileNowPreviewNoticeVm.additionalNotes}
                  />
                </Grid>
              </Box>
            );
          })()}

          {/* Document Attachments Table */}
          {previewData?.attachments?.some(
            (att: FileAttachment) => att.file !== null,
          ) &&
            (() => {
              const attachmentRows = previewData.attachments
                .filter((att: FileAttachment) => att.file !== null)
                .map((attachment: FileAttachment, index: number) => ({
                  id: attachment.id || index,
                  type: attachment.type,
                  documentName: attachment.file?.name || "N/A",
                  file: attachment.file,
                }));

              const columns: GridColDef[] = [
                {
                  field: "download",
                  headerName: "Download",
                  width: 180,
                  align: "center",
                  headerAlign: "center",
                  sortable: false,
                  filterable: false,
                  renderCell: (params) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          params.row.file && handleViewFile(params.row.file)
                        }
                        sx={{ color: "#0d4a63" }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          params.row.file && handleDownloadFile(params.row.file)
                        }
                        sx={{ color: "#0d4a63" }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ),
                },
                {
                  field: "type",
                  headerName: "Type",
                  width: 250,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {params.value}
                    </Box>
                  ),
                },
                {
                  field: "documentName",
                  headerName: "Document Name",
                  flex: 1,
                  minWidth: 300,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => {
                    const name = String(params.value ?? "");
                    return (
                      <Tooltip title={name} arrow>
                        <Typography
                          noWrap
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "center",
                          }}
                        >
                          {name}
                        </Typography>
                      </Tooltip>
                    );
                  },
                },
              ];

              return (
                <Box sx={{ marginTop: "32px", px: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Typography variant="overline" sx={noticeSectionTitleSx}>
                      Document Attachments
                    </Typography>
                    <Button
                      startIcon={
                        downloadingAll ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      onClick={handleDownloadAll}
                      disabled={downloadingAll}
                      sx={{
                        borderColor: "#2099d2",
                        color: "#15607a",
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                      variant="outlined"
                    >
                      {downloadingAll ? "Downloading..." : "Download All"}
                    </Button>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      ...noticeScreenCardSx,
                      width: "100%",
                      p: 3,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: 56 + attachmentRows.length * 52,
                        width: "100%",
                      }}
                    >
                      <DataGrid
                        rows={attachmentRows}
                        columns={columns}
                        hideFooter
                        disableColumnMenu
                        disableSelectionOnClick
                        getRowHeight={() => 52}
                        sx={{
                          border: "none",
                          "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #e0e0e0",
                            borderRight: "1px solid #e0e0e0",
                            padding: "12px 24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                          "& .MuiDataGrid-cell:last-of-type": {
                            borderRight: "none",
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#0d4a63",
                            color: "#ffffff",
                            fontWeight: "bold",
                            borderBottom: "2px solid #083d52",
                            minHeight: "56px !important",
                            maxHeight: "56px !important",
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "14px",
                          },
                          "& .MuiDataGrid-columnHeader": {
                            borderRight: "1px solid #083d52",
                            padding: "12px 24px",
                            minHeight: "56px !important",
                            maxHeight: "56px !important",
                          },
                          "& .MuiDataGrid-columnHeader:last-of-type": {
                            borderRight: "none",
                          },
                          "& .MuiDataGrid-row": {
                            "&:nth-of-type(even)": {
                              backgroundColor: "#fafafa",
                            },
                          },
                          "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                          "& .MuiDataGrid-cell[data-field='documentName']": {
                            overflow: "hidden",
                            minWidth: 0,
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Box>
              );
            })()}

          <Box sx={{ px: 3, pb: 2 }}>
            <Grid container spacing={3}>
              <NoticeStatusCard items={getNoticeStatusItems(selectedRow)} />
            </Grid>
          </Box>
        </DialogContent>
      ) : (
        // Edit Mode - Original Form
        <Formik<FileNowFormValues>
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          validateOnChange
          validateOnBlur
          onSubmit={handleFormSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue, validateForm }) => (
            <Form>
              <FileNowFormEffects
                scheduleDataByType={scheduleDataByType}
                canFileAsZeroReport={canFileAsZeroReport}
                typeOfReport={values.typeOfReport}
                selectTypeData={selectTypeData}
                applyTypeData={applyTypeData}
                setFieldValue={setFieldValue}
                fieldRequirements={fieldRequirements}
                validateForm={validateForm}
              />
              <DialogContent dividers>
                <HeaderInfo
                  selectedRow={selectedRow as StateReportModel}
                  states={states}
                />

                {/* Form Section */}
                <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
                  {/* Type of Report */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.typeOfReport && !!errors.typeOfReport}
                    >
                      <FormLabel required>Type of Report</FormLabel>
                      <Select
                        value={values.typeOfReport}
                        onChange={(e: SelectChangeEvent) => {
                          const selectedType = e.target.value;
                          setFieldValue("typeOfReport", selectedType);
                          if (selectedType) {
                            const typeData = selectTypeData(selectedType);
                            applyTypeData(typeData, setFieldValue);
                          } else {
                            // Reset type-dependent values when switching back to "Choose an item"
                            setReportInvoiceRequired(false);
                            setReconciliationListRequired(false);
                            setFieldRequirements(EMPTY_FILING_FIELD_REQUIREMENTS);
                            setFieldValue("taxAmount", selectedRow?.taxAmount || "");
                            setFieldValue("paymentType", selectedRow?.paymentType || "");
                            setFieldValue("payTo", "");
                            setFieldValue("address", "");
                            setFieldValue("instructions", "");
                            setFieldValue("filingUrl", "");
                            setFieldValue("additionalNotes", "");
                            setFieldValue("largeReport", "false");
                            setFieldValue("largeReportFee", "");
                          }
                        }}
                        displayEmpty
                        sx={{ marginTop: "8px" }}
                      >
                        <MenuItem value="">Choose an item.</MenuItem>
                        <MenuItem value="Premium-Bearing">
                          Premium-Bearing
                        </MenuItem>
                        {canFileAsZeroReport && (
                          <MenuItem value="Zero Report">Zero Report</MenuItem>
                        )}
                      </Select>
                      <FormHelperText
                        error={touched.typeOfReport && !!errors.typeOfReport}
                      >
                        {touched.typeOfReport &&
                        errors.typeOfReport &&
                        typeof errors.typeOfReport === "string"
                          ? errors.typeOfReport
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Tax Amount */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.taxAmount && !!errors.taxAmount}
                    >
                      <FormLabel required={fieldRequirements.taxAmount}>
                        Tax Amount{fieldRequirements.taxAmount ? "" : " (Optional)"}
                      </FormLabel>
                      <Box
                        sx={{
                          marginTop: "8px",
                          backgroundColor: "#FFF9C4",
                          borderRadius: "4px",
                          overflow: "hidden",
                          boxShadow: "none",
                          outline: "none",
                          "& *": {
                            boxShadow: "none !important",
                            outline: "none",
                          },
                          "&:focus": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          "&:focus-visible": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent !important",
                            borderRadius: "4px",
                            boxShadow: "none",
                            "& fieldset": {
                              borderRadius: "4px",
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                            "&:hover": {
                              boxShadow: "none",
                              backgroundColor: "transparent !important",
                              "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                            },
                            "&.Mui-focused": {
                              boxShadow: "none",
                              backgroundColor: "transparent !important",
                              "& fieldset": {
                                borderColor: "#1976d2",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: "4px",
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                            "& .MuiOutlinedInput-input": {
                              backgroundColor: "transparent",
                            },
                          },
                        }}
                      >
                        <TextField
                          name="taxAmount"
                          value={values.taxAmount}
                          onChange={handleChange}
                          placeholder="$000,000.00"
                          fullWidth
                          sx={{
                            boxShadow: "none",
                            margin: 0,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              margin: 0,
                            },
                            "& .MuiFormControl-root": {
                              margin: 0,
                            },
                          }}
                          InputProps={{
                            inputComponent: NumericFormatCustom as any,
                          }}
                        />
                      </Box>
                      <FormHelperText
                        error={touched.taxAmount && !!errors.taxAmount}
                      >
                        {touched.taxAmount &&
                        errors.taxAmount &&
                        typeof errors.taxAmount === "string"
                          ? errors.taxAmount
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Payment Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FormLabel>Payment Type (Optional)</FormLabel>
                      <TextField
                        name="paymentType"
                        value={values.paymentType}
                        onChange={handleChange}
                        placeholder="Click or tap here to enter text."
                        sx={{ marginTop: "8px" }}
                      />
                    </FormControl>
                  </Grid>

                  {/* Pay To */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.payTo && !!errors.payTo}
                    >
                      <FormLabel required={fieldRequirements.payTo}>
                        Pay To{fieldRequirements.payTo ? "" : " (Optional)"}
                      </FormLabel>
                      <TextField
                        name="payTo"
                        value={values.payTo}
                        onChange={handleChange}
                        placeholder="Click or tap here to enter text."
                        sx={{ marginTop: "8px" }}
                      />
                      <FormHelperText error={touched.payTo && !!errors.payTo}>
                        {touched.payTo &&
                        errors.payTo &&
                        typeof errors.payTo === "string"
                          ? errors.payTo
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.address && !!errors.address}
                    >
                      <FormLabel required={fieldRequirements.address}>
                        Address{fieldRequirements.address ? "" : " (Optional)"}
                      </FormLabel>
                      <TextField
                        name="address"
                        value={values.address}
                        onChange={handleChange}
                        placeholder="Click or tap here to enter text."
                        sx={{ marginTop: "8px" }}
                      />
                      <FormHelperText error={touched.address && !!errors.address}>
                        {touched.address &&
                        errors.address &&
                        typeof errors.address === "string"
                          ? errors.address
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Large Report */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.largeReport && !!errors.largeReport}
                      sx={{ marginTop: "8px" }}
                    >
                      <FormLabel required>Large Report</FormLabel>
                      <RadioGroup
                        row
                        value={values.largeReport}
                        onChange={(e) => {
                          setFieldValue("largeReport", e.target.value);
                          if (e.target.value !== "true") {
                            setFieldValue("largeReportFee", "");
                          }
                        }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio />}
                          label="True"
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio />}
                          label="False"
                        />
                      </RadioGroup>
                      <FormHelperText
                        error={touched.largeReport && !!errors.largeReport}
                      >
                        {touched.largeReport &&
                        errors.largeReport &&
                        typeof errors.largeReport === "string"
                          ? errors.largeReport
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {values.largeReport === "true" && (
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={touched.largeReportFee && !!errors.largeReportFee}
                      >
                        <FormLabel required>Large Report Fee</FormLabel>
                        <TextField
                          name="largeReportFee"
                          value={values.largeReportFee}
                          onChange={handleChange}
                          placeholder="$0.00"
                          fullWidth
                          sx={{ marginTop: "8px" }}
                          InputProps={{
                            inputComponent: NumericFormatCustom as any,
                          }}
                        />
                        <FormHelperText
                          error={touched.largeReportFee && !!errors.largeReportFee}
                        >
                          {touched.largeReportFee &&
                          errors.largeReportFee &&
                          typeof errors.largeReportFee === "string"
                            ? errors.largeReportFee
                            : "Enter the fee amount billed for this large report."}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Instructions */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={touched.instructions && !!errors.instructions}
                    >
                      <FormLabel required={fieldRequirements.instructions}>
                        Instructions{fieldRequirements.instructions ? "" : " (Optional)"}
                      </FormLabel>
                      <TextField
                        name="instructions"
                        value={values.instructions}
                        onChange={handleChange}
                        placeholder={
                          !values.typeOfReport
                            ? "Select Type of Report to view Instructions"
                            : "Click or tap here to enter text."
                        }
                        disabled={!values.typeOfReport}
                        multiline
                        rows={4}
                        sx={{ marginTop: "8px" }}
                      />
                      <FormHelperText
                        error={touched.instructions && !!errors.instructions}
                      >
                        {touched.instructions &&
                        errors.instructions &&
                        typeof errors.instructions === "string"
                          ? errors.instructions
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Filing URL - text box style, blue clickable link, not editable */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={touched.filingUrl && !!errors.filingUrl}
                    >
                      <FormLabel required={fieldRequirements.filingUrl}>
                        URL{fieldRequirements.filingUrl ? "" : " (Optional)"}
                      </FormLabel>
                      <Box
                        sx={{
                          marginTop: "8px",
                          padding: "16.5px 14px",
                          minHeight: "56px",
                          border: "1px solid",
                          borderColor:
                            touched.filingUrl && errors.filingUrl
                              ? "error.main"
                              : "divider",
                          borderRadius: "4px",
                          backgroundColor: "background.paper",
                          "&:hover": {
                            borderColor: values.filingUrl ? "primary.main" : undefined,
                          },
                        }}
                      >
                        {!values.typeOfReport ? (
                          <Typography color="text.secondary" variant="body1">
                            Select Type of Report to view URL
                          </Typography>
                        ) : values.filingUrl ? (
                          <Link
                            href={values.filingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "primary.main",
                              wordBreak: "break-all",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {values.filingUrl}
                          </Link>
                        ) : (
                          <Typography color="text.secondary" variant="body1">
                           N/A
                          </Typography>
                        )}
                      </Box>
                      <FormHelperText
                        error={touched.filingUrl && !!errors.filingUrl}
                      >
                        {touched.filingUrl &&
                        errors.filingUrl &&
                        typeof errors.filingUrl === "string"
                          ? errors.filingUrl
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Additional Notes */}
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={touched.additionalNotes && !!errors.additionalNotes}
                    >
                      <FormLabel required={fieldRequirements.additionalNotes}>
                        Additional Notes
                        {fieldRequirements.additionalNotes ? "" : " (Optional)"}
                      </FormLabel>
                      <TextField
                        name="additionalNotes"
                        value={values.additionalNotes}
                        onChange={handleChange}
                        placeholder="Click or tap here to enter text."
                        multiline
                        rows={4}
                        sx={{ marginTop: "8px" }}
                      />
                      <FormHelperText
                        error={touched.additionalNotes && !!errors.additionalNotes}
                      >
                        {touched.additionalNotes &&
                        errors.additionalNotes &&
                        typeof errors.additionalNotes === "string"
                          ? errors.additionalNotes
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Document Attachments Section */}
                <Box sx={{ marginTop: "32px" }}>
                  <FormLabel
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "16px",
                      display: "block",
                    }}
                  >
                    Document Attachments:
                  </FormLabel>
                  <Box
                    sx={{
                      marginBottom: "16px",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Please select a box to choose a file from your computer, or
                    drag and drop your file into the appropriate box below:
                  </Box>

                  <Grid container spacing={2}>
                    {attachments.map((attachment, index) => {
                      const isRequired =
                        (attachment.type === "Report or Invoice" && reportInvoiceRequired) ||
                        (attachment.type === "Reconciliation List" && reconciliationListRequired);
                      return (
                        <Grid item xs={12} sm={6} key={attachment.id}>
                          <Box>
                            <Box
                              sx={{ marginBottom: "8px", fontWeight: "medium" }}
                            >
                              {attachment.type}
                              {isRequired && (
                                <Box component="span" sx={{ color: "error.main" }}> *</Box>
                              )}
                            </Box>
                            <FileDropzone
                              label={attachment.type}
                              onFileDrop={(file) => handleFileDrop(index, file)}
                              acceptedFile={attachment.file}
                              allowExcel={attachment.type === "Reconciliation List"}
                              onViewFile={
                                attachment.file ? handleViewFile : undefined
                              }
                            />
                            {attachment.file && (
                              <Box sx={{ marginTop: "8px", textAlign: "center" }}>
                                <Link
                                  component="button"
                                  variant="body2"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeAttachment(index);
                                  }}
                                  sx={{ color: "#d32f2f", cursor: "pointer" }}
                                >
                                  Remove
                                </Link>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {attachments.length < 12 && (
                    <Box sx={{ marginTop: "16px" }}>
                      <Link
                        component="button"
                        variant="body1"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addMoreAttachments();
                        }}
                        sx={{
                          color: "#1976D2",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: "14px",
                          fontWeight: "medium",
                        }}
                      >
                        + Add More Supplemental Attachments
                      </Link>
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions
                sx={{ padding: "16px 24px", justifyContent: "space-between" }}
              >
                <Button
                  onClick={handleClose}
                  sx={{
                    padding: "10px 20px",
                    backgroundColor: "#E0E0E0",
                    color: "#000",
                    "&:hover": {
                      backgroundColor: "#D0D0D0",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    const formik = formikRef.current;
                    if (!formik) return;
                    const validationErrors = await formik.validateForm();
                    const errorKeys = Object.keys(validationErrors);
                    if (errorKeys.length > 0) {
                      formik.setTouched(
                        errorKeys.reduce<Record<string, boolean>>((acc, key) => {
                          acc[key] = true;
                          return acc;
                        }, {}),
                      );
                      const firstMsg = validationErrors[errorKeys[0] as keyof FileNowFormValues];
                      await Swal.fire({
                        icon: "warning",
                        title: "Required fields",
                        text:
                          typeof firstMsg === "string"
                            ? firstMsg
                            : "Please complete all required fields before continuing.",
                      });
                      return;
                    }
                    handleFormSubmit(formik.values);
                  }}
                  sx={{
                    padding: "10px 20px",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#45a049",
                    },
                    borderRadius: "4px",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  File Now
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}

      {/* Preview Mode Footer Actions */}
      {isPreviewMode && (
        <>
          <DialogActions
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Footer Message */}
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Are you ready to publish to the Client Portal?
            </Typography>
          </DialogActions>
          <DialogActions
            sx={{ padding: "16px 24px", justifyContent: "space-between" }}
          >
            <Button
              onClick={handleContinueEdit}
              sx={{
                padding: "10px 20px",
                backgroundColor: "#FF9800",
                color: "white",
                "&:hover": {
                  backgroundColor: "#F57C00",
                },
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              No, Continue to EDIT
            </Button>
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
              sx={{
                padding: "10px 20px",
                backgroundColor: isPublishing ? "#9E9E9E" : "#4CAF50",
                color: "white",
                "&:hover": {
                  backgroundColor: isPublishing ? "#9E9E9E" : "#45a049",
                },
                "&:disabled": {
                  backgroundColor: "#9E9E9E",
                  color: "white",
                },
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Yes, Proceed to PUBLISH
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>

    <StateReportDocumentViewerModal
      open={documentViewerOpen}
      onClose={handleCloseDocumentViewer}
      fileName={documentViewerFileName}
      loading={documentViewerLoading}
      viewerUrl={documentViewerUrl}
    />

    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StateReportFilerNotes
        open={filerNotesOpen}
        mappingActionType="no_action"
        handleClose={() => {
          setFilerNotesOpen(false);
          setFilingScheduleTaxAmount(null);
          setNcaScheduleMessaging(null);
          handleClose();
        }}
        currentStatus={selectedRow?.status ?? "Filed"}
        handleSubmit={async (data: FiledNotesSubmitData) => {
          if (!selectedRow) return false;

          const noteDateStr = formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY");
          const noteDateForApi = data.noteDate ? mstDateStringToUtcIsoString(data.noteDate) : new Date().toISOString();
          const typeOfReport =
            ncaScheduleMessaging?.typeOfReport ||
            String((selectedRow as any).typeOfReport ?? "").trim() ||
            "";
          const instructions = ncaScheduleMessaging?.instructions || "";
          const notesToClient = ncaScheduleMessaging?.notesToClient || "";
          const filesToUpload = data.attachments ?? [];

          const completePayload = {
            ...selectedRow,
            status: "Filed",
            partnerId: selectedRow.partnersId ?? (selectedRow as any).partnerId,
            stateReportId: selectedRow.stateReportId ?? 0,
            scheduleId: selectedRow.scheduleId,
            filerName: `${user?.userFirstName ?? ""} ${user?.userLastName || (user as any).userlastName || ""}`.trim(),
            filerDate: mstDateStringToUtcIsoString(noteDateStr),
            notes: data.noteDetail,
            policySubStatus: data.policySubStatus,
            dateDeclarationFiled: data.dateDeclarationFiled,
            firstRequest: data.firstRequestDate || null,
            secondRequest: data.secondRequestDate || null,
            thirdRequest: data.thirdRequestDate || null,
            notesDate: noteDateForApi,
            suspended: data.suspendedDate || null,
            taxAmount: data.taxAmount,
            dateFinalReportSentToClient: null,
            largeReport: data.largeReport,
            largeReportFee: data.largeReportFee ?? "",
            // Workflow marker — do not overwrite with MISC free-text note.
            additionalNotes: NO_ACTION_REQUIRED_MARKER,
            instructions,
            notesToClient,
            typeOfReport,
          } as any;

          if (completePayload.stateReportId === null) {
            completePayload.stateReportId = 0;
          }

          try {
            let res: any;
            if (filesToUpload.length > 0) {
              const formData = new FormData();
              formData.append("data", JSON.stringify(completePayload));
              filesToUpload.forEach((file, index) => {
                formData.append(`file_${index}`, file);
                formData.append(`fileType_${index}`, "Supplemental Attachment");
                formData.append(`fileName_${index}`, file.name);
              });
              formData.append("fileCount", filesToUpload.length.toString());
              res = await apiClient("/StateReport/AddEditStateReportWithFiles", {
                method: "POST",
                headers: new Headers({
                  Authorization: `Bearer ${user?.token}`,
                }),
                body: formData,
              });
            } else {
              res = await apiClient("/StateReport/AddEditStateReport", {
                method: "POST",
                headers: new Headers({
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user?.token}`,
                }),
                body: JSON.stringify([completePayload]),
              });
            }

            if (res && res.status) {
              // Same order as File Now Publish: success Swal while dialog is still open, then close.
              await Swal.fire({
                title: "Success!",
                text: "Status updated successfully.",
                icon: "success",
                confirmButtonText: "OK",
              });
              setFilerNotesOpen(false);
              setFilingScheduleTaxAmount(null);
              setNcaScheduleMessaging(null);
              handleClose();
              handleSubmit?.({
                typeOfReport,
                taxAmount: data.taxAmount ?? "",
                paymentType: "",
                payTo: "",
                address: "",
                instructions,
                additionalNotes: NO_ACTION_REQUIRED_MARKER,
                largeReport: data.largeReport ? "true" : "false",
                largeReportFee: data.largeReportFee ?? "",
                attachments: [],
              });
              return true;
            }
            throw new Error(res?.message ?? "Failed to update status.");
          } catch (error: any) {
            console.error("Error updating state report:", error);
            await Swal.fire({
              title: "Error!",
              text: error?.message ?? "Failed to update status. Please try again.",
              icon: "error",
              confirmButtonText: "OK",
            });
            return false;
          }
        }}
        selectedRows={
          selectedRow
            ? [
                {
                  ...selectedRow,
                  taxAmount:
                    filingScheduleTaxAmount ??
                    selectedRow.taxAmount ??
                    "",
                } as StateReportModel,
              ]
            : null
        }
      />
    </LocalizationProvider>
    </>
  );
};

export default StateReportFileNowDialog;
